import { AnonymousStudentProfileUpdated, AnonymousStudentServiceReady } from '@studyportals/anonymous-student-interfaces';

import {
	IEventAggregationService,
	ISubscriber,
} from '@studyportals/event-aggregation-service-interface';
import { StudentField } from '@studyportals/studentdomain';
import store from '@/store';
import globals from '@/utils/global';
import DisciplineFacetsRepository from '@/repositories/DisciplineFacetsRepository';
import ResultsRepository from '@/repositories/ResultsRepository';
import IUserData from '@/interfaces/user-data.interface';
import { DataStorage } from '@studyportals/data-storage';
import { CurrencyConversionServiceReadyEvent, LocalizedContentServiceReadyEvent } from '@studyportals/portal-localized-content';
import { SearchPageRouterReadyEvent  } from '@studyportals/search-page-interface';
import { FilterValuesChangedEvent } from '@studyportals/search-filters';
import PathUtils from '@/utils/PathUtils';
import SuggestionsRepository from '@/repositories/SuggestionsRepository';

export class DependencyCheckerPlugin implements
	ISubscriber<AnonymousStudentServiceReady |
	AnonymousStudentProfileUpdated |
	LocalizedContentServiceReadyEvent |
	CurrencyConversionServiceReadyEvent |
	SearchPageRouterReadyEvent |
	FilterValuesChangedEvent>
{
	private localizedContentServiceIsReady = false;
	private currencyConversionServiceIsReady = false;

	private get eventAggregationService(): IEventAggregationService | null {
		if (typeof window['EventAggregationService'] === 'undefined') {
			return null;
		}
		return window['EventAggregationService'];
	}

	public initialize(): void {
		globals.disciplineFacetsRepository = new DisciplineFacetsRepository();
		globals.resultsRepository = new ResultsRepository();
		globals.suggestionsRepository = new SuggestionsRepository();

		if (!this.eventAggregationService) {
			return;
		}

		this.eventAggregationService.subscribeTo(
			AnonymousStudentServiceReady.EventType,
			this,
			true,
		);


		this.eventAggregationService.subscribeTo(
			AnonymousStudentProfileUpdated.EventType,
			this,
			false,
		);

		this.eventAggregationService.subscribeTo(
			LocalizedContentServiceReadyEvent.EventType,
			this,
			true,
		);

		this.eventAggregationService.subscribeTo(
			CurrencyConversionServiceReadyEvent.EventType,
			this,
			true,
		);

		this.eventAggregationService.subscribeTo(
			SearchPageRouterReadyEvent.EventType,
			this,
			false
		);
		window.addEventListener('storage', (event) => {
			void (async (): Promise<void> => {
				if (event.key === 'global_access_initiative_questionnaire_taken') {
					const studentData = await globals.anonymousStudent.getStudentData([StudentField.RESIDENCE_COUNTRY_ID]);
					const countryOfResidenceId = studentData[StudentField.RESIDENCE_COUNTRY_ID];

					if (countryOfResidenceId) {
						store.mutations.setUserData({ countryOfResidence: countryOfResidenceId })
						store.mutations.setGAIQuestionnaireTaken(DataStorage.retrieve(event.key) === 'true');
					}
				}
			})();
		});

		if (!PathUtils.isInSearchPage()) return;

		this.eventAggregationService.subscribeTo(
			FilterValuesChangedEvent.EventType,
			this,
			false
		);
	}

	public notify(event: AnonymousStudentServiceReady |
		AnonymousStudentProfileUpdated |
		LocalizedContentServiceReadyEvent |
		CurrencyConversionServiceReadyEvent |
		SearchPageRouterReadyEvent |
		FilterValuesChangedEvent
	): void {
		switch (event.eventType) {
		case AnonymousStudentServiceReady.EventType:
			this.handleAnonymousStudentReady(event as AnonymousStudentServiceReady);
			break;
		case AnonymousStudentProfileUpdated.EventType:
			this.handleAnonymousStudentProfileUpdate();
			break;
		case LocalizedContentServiceReadyEvent.EventType:
			this.handleLocalizedContentServiceReadyEvent(event as LocalizedContentServiceReadyEvent);
			break;
		case CurrencyConversionServiceReadyEvent.EventType:
			this.handleCurrencyConversionServiceReadyEvent(event as CurrencyConversionServiceReadyEvent);
			break;
		case SearchPageRouterReadyEvent.EventType:
			this.handleSearchPageReady(event as SearchPageRouterReadyEvent);
			break;
		case FilterValuesChangedEvent.EventType:
			this.handleFilterValuesChanged(event as FilterValuesChangedEvent);
			break;
		}

		if (this.localizedContentServiceIsReady && this.currencyConversionServiceIsReady) {
			store.mutations.setDependenciesReady(true);
		}
	}

	private handleAnonymousStudentReady(
		event: AnonymousStudentServiceReady,
	): void {
		globals.anonymousStudent = event.anonymousStudentService;
		void this.updateStudentData();
	}

	private handleAnonymousStudentProfileUpdate(): void {
		void this.updateStudentData();
	}

	private async updateStudentData(): Promise<void> {
		const data = await globals.anonymousStudent.getStudentData([
			StudentField.NATIONALITY_COUNTRY_ID,
			StudentField.CURRENCY,
			StudentField.RESIDENCE_COUNTRY_ID
		]);

		const userData: Partial<IUserData> = {};

		if (data[StudentField.NATIONALITY_COUNTRY_ID]) {
			userData.nationality = data[StudentField.NATIONALITY_COUNTRY_ID];
		}

		if (data[StudentField.CURRENCY]) {
			userData.currency = data[StudentField.CURRENCY];
		}

		if (data[StudentField.RESIDENCE_COUNTRY_ID]) {
			userData.countryOfResidence = data[StudentField.RESIDENCE_COUNTRY_ID];
		}

		store.mutations.setUserData(userData);
	}

	private handleLocalizedContentServiceReadyEvent(event: LocalizedContentServiceReadyEvent): void {
		globals.localizedContentService = event.localizedContentService;
		this.localizedContentServiceIsReady = true;
	}

	private handleCurrencyConversionServiceReadyEvent(event: CurrencyConversionServiceReadyEvent): void {
		globals.currencyConversionService = event.currencyConversionService;
		this.currencyConversionServiceIsReady = true;
	}

	private handleSearchPageReady(event: SearchPageRouterReadyEvent): void {
		globals.searchPageService = event.routing;
	}

	private handleFilterValuesChanged({programmeSearchInput, filterProvider}: FilterValuesChangedEvent): void {
		store.mutations.setProgrammeSearchInput(programmeSearchInput);
		store.mutations.setFilterProvider(filterProvider);
		// void store.actions.updateProgrammesResults();
	}
}